import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import inventoryService from "../api/services/inventoryService";
import { useAuth } from "./AuthContext";

// Create context
const InventoryContext = createContext();

// Custom hook for using this context
export const useInventory = () => useContext(InventoryContext);

// Provider component
export const InventoryProvider = ({ children }) => {
  const { user } = useAuth();

  // State for inventory items
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for stock levels
  const [stockLevels, setStockLevels] = useState([]);
  const [loadingStockLevels, setLoadingStockLevels] = useState(false);
  const [stockError, setStockError] = useState(null);

  // State for inventory movements
  const [movements, setMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);
  const [movementsError, setMovementsError] = useState(null);

  // State for inventory locations
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locationsError, setLocationsError] = useState(null);

  // State for inventory transfers
  const [transfers, setTransfers] = useState([]);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [transfersError, setTransfersError] = useState(null);

  // State for inventory valuation
  const [valuation, setValuation] = useState(null);
  const [loadingValuation, setLoadingValuation] = useState(false);
  const [valuationError, setValuationError] = useState(null);

  // State for inventory summary
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  // State for reorder suggestions
  const [reorderSuggestions, setReorderSuggestions] = useState([]);
  const [loadingReorderSuggestions, setLoadingReorderSuggestions] =
    useState(false);
  const [reorderSuggestionsError, setReorderSuggestionsError] = useState(null);

  // Load data when user changes. Guarded by a ref that stores the last
  // user id we fetched for so React StrictMode's double-invocation and
  // upstream context re-renders don't re-fire the (5) network calls.
  const fetchedForUserId = useRef(null);
  useEffect(() => {
    const uid = user?.id || user?._id || null;
    if (!uid) { fetchedForUserId.current = null; return; }
    if (fetchedForUserId.current === uid) return;
    fetchedForUserId.current = uid;
    loadInventory();
    loadStockLevels();
    loadLocations();
    loadInventorySummary();
    loadReorderSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Load inventory items from API
  const loadInventory = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await inventoryService.getInventory();
      setInventory(data);
    } catch (err) {
      console.error("Error loading inventory:", err);
      setError("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  // Load current stock levels
  const loadStockLevels = async () => {
    setLoadingStockLevels(true);
    setStockError(null);
    try {
      const data = await inventoryService.getStockLevels();
      setStockLevels(data);
    } catch (err) {
      console.error("Error loading stock levels:", err);
      setStockError("Failed to load stock levels");
    } finally {
      setLoadingStockLevels(false);
    }
  };

  // Load inventory movements history
  const loadMovements = async (params = {}) => {
    setLoadingMovements(true);
    setMovementsError(null);
    try {
      const data = await inventoryService.getInventoryMovements(params);
      setMovements(data);
    } catch (err) {
      console.error("Error loading inventory movements:", err);
      setMovementsError("Failed to load inventory movements");
    } finally {
      setLoadingMovements(false);
    }
  };

  // Create a new inventory item
  const createInventoryItem = async (itemData) => {
    try {
      const newItem = await inventoryService.createInventoryItem(itemData);
      setInventory([...inventory, newItem]);
      return newItem;
    } catch (err) {
      console.error("Error creating inventory item:", err);
      throw err;
    }
  };

  // Update an inventory item
  const updateInventoryItem = async (id, itemData) => {
    try {
      const updatedItem = await inventoryService.updateInventoryItem(
        id,
        itemData
      );
      setInventory(
        inventory.map((item) => (item.id === id ? updatedItem : item))
      );
      return updatedItem;
    } catch (err) {
      console.error("Error updating inventory item:", err);
      throw err;
    }
  };

  // Delete an inventory item
  const deleteInventoryItem = async (id) => {
    try {
      await inventoryService.deleteInventoryItem(id);
      setInventory(inventory.filter((item) => item.id !== id));
    } catch (err) {
      console.error("Error deleting inventory item:", err);
      throw err;
    }
  };

  // Record an inventory adjustment
  const recordAdjustment = async (adjustmentData) => {
    try {
      const result = await inventoryService.recordAdjustment(adjustmentData);
      // Refresh stock levels after adjustment
      await loadStockLevels();
      // Refresh movements after adjustment
      await loadMovements();
      // Refresh summary after adjustment
      await loadInventorySummary();
      return result;
    } catch (err) {
      console.error("Error recording inventory adjustment:", err);
      throw err;
    }
  };

  // Get low stock items (items below reorder level)
  const getLowStockItems = () => {
    return stockLevels.filter(
      (item) => item.currentQuantity <= item.reorderLevel
    );
  };

  // Load inventory locations
  const loadLocations = async () => {
    setLoadingLocations(true);
    setLocationsError(null);
    try {
      const data = await inventoryService.getLocations();
      setLocations(data);
    } catch (err) {
      console.error("Error loading inventory locations:", err);
      setLocationsError("Failed to load inventory locations");
    } finally {
      setLoadingLocations(false);
    }
  };

  // Create a new inventory location
  const createLocation = async (locationData) => {
    try {
      const newLocation = await inventoryService.createLocation(locationData);
      setLocations([...locations, newLocation]);
      return newLocation;
    } catch (err) {
      console.error("Error creating inventory location:", err);
      throw err;
    }
  };

  // Update an inventory location
  const updateLocation = async (id, locationData) => {
    try {
      const updatedLocation = await inventoryService.updateLocation(
        id,
        locationData
      );
      setLocations(
        locations.map((location) =>
          location.id === id ? updatedLocation : location
        )
      );
      return updatedLocation;
    } catch (err) {
      console.error("Error updating inventory location:", err);
      throw err;
    }
  };

  // Load inventory transfers
  const loadTransfers = async (params = {}) => {
    setLoadingTransfers(true);
    setTransfersError(null);
    try {
      const data = await inventoryService.getAllTransfers(params);
      setTransfers(data);
    } catch (err) {
      console.error("Error loading inventory transfers:", err);
      setTransfersError("Failed to load inventory transfers");
    } finally {
      setLoadingTransfers(false);
    }
  };

  // Record an inventory transfer
  const recordTransfer = async (transferData) => {
    try {
      const result = await inventoryService.recordTransfer(transferData);
      // Refresh stock levels after transfer
      await loadStockLevels();
      // Refresh transfers list
      await loadTransfers();
      // Refresh movements after transfer
      await loadMovements();
      // Refresh summary after transfer
      await loadInventorySummary();
      return result;
    } catch (err) {
      console.error("Error recording inventory transfer:", err);
      throw err;
    }
  };

  // Load inventory valuation
  const loadValuation = async (params = {}) => {
    setLoadingValuation(true);
    setValuationError(null);
    try {
      const data = await inventoryService.getValuation(params);
      setValuation(data);
    } catch (err) {
      console.error("Error loading inventory valuation:", err);
      setValuationError("Failed to load inventory valuation");
    } finally {
      setLoadingValuation(false);
    }
  };

  // Load inventory summary with KPIs
  const loadInventorySummary = async () => {
    setLoadingSummary(true);
    setSummaryError(null);
    try {
      const data = await inventoryService.getInventorySummary();
      setSummary(data);
    } catch (err) {
      console.error("Error loading inventory summary:", err);
      setSummaryError("Failed to load inventory summary");
    } finally {
      setLoadingSummary(false);
    }
  };

  // Load reorder suggestions
  const loadReorderSuggestions = async () => {
    setLoadingReorderSuggestions(true);
    setReorderSuggestionsError(null);
    try {
      const data = await inventoryService.getReorderSuggestions();
      setReorderSuggestions(data);
    } catch (err) {
      console.error("Error loading reorder suggestions:", err);
      setReorderSuggestionsError("Failed to load reorder suggestions");
    } finally {
      setLoadingReorderSuggestions(false);
    }
  };

  // Get stock impact of a sales transaction
  const getSalesStockImpact = async (saleId) => {
    try {
      return await inventoryService.getSalesStockImpact(saleId);
    } catch (err) {
      console.error(`Error getting stock impact for sale ${saleId}:`, err);
      throw err;
    }
  };

  // Get stock impact of a purchase transaction
  const getPurchaseStockImpact = async (purchaseId) => {
    try {
      return await inventoryService.getPurchaseStockImpact(purchaseId);
    } catch (err) {
      console.error(
        `Error getting stock impact for purchase ${purchaseId}:`,
        err
      );
      throw err;
    }
  };

  // Get inventory batches and lot tracking
  const getBatchesAndLots = async (productId) => {
    try {
      return await inventoryService.getBatchesAndLots(productId);
    } catch (err) {
      console.error(`Error getting batches for product ${productId}:`, err);
      throw err;
    }
  };

  // Context value to be provided
  const value = {
    // Inventory items
    inventory,
    loading,
    error,
    loadInventory,
    createInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,

    // Stock levels
    stockLevels,
    loadingStockLevels,
    stockError,
    loadStockLevels,
    getLowStockItems,

    // Inventory movements
    movements,
    loadingMovements,
    movementsError,
    loadMovements,

    // Inventory adjustments
    recordAdjustment,

    // Inventory locations
    locations,
    loadingLocations,
    locationsError,
    loadLocations,
    createLocation,
    updateLocation,

    // Inventory transfers
    transfers,
    loadingTransfers,
    transfersError,
    loadTransfers,
    recordTransfer,

    // Inventory valuation
    valuation,
    loadingValuation,
    valuationError,
    loadValuation,

    // Inventory summary
    summary,
    loadingSummary,
    summaryError,
    loadInventorySummary,

    // Reorder suggestions
    reorderSuggestions,
    loadingReorderSuggestions,
    reorderSuggestionsError,
    loadReorderSuggestions,

    // Integration with sales and purchases
    getSalesStockImpact,
    getPurchaseStockImpact,

    // Batch and lot tracking
    getBatchesAndLots,
  };

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
};

export default InventoryContext;
